import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import Modal from '../components/Modal';
import { useToast } from '../context/ToastContext';
import './SMEStyles.css';

const UsersIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
);
const CheckIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);
const BanIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
);
const ClockIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);

const MyExaminers = () => {
    const [loading, setLoading] = useState(true);
    const [examiners, setExaminers] = useState([]);
    const [filter, setFilter] = useState('all');
    const [modalConfig, setModalConfig] = useState({ isOpen: false });
    const navigate = useNavigate();
    const { showToast } = useToast();

    useEffect(() => {
        fetchExaminers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchExaminers = async () => {
        try {
            setLoading(true);
            const { data } = await API.get('/users/my-examiners');
            setExaminers(data.examiners || []);
        } catch (error) {
            console.error('Error fetching examiners:', error);
            if (error.response?.status === 403) {
                showToast('Access denied. Only Subject Matter Experts can view this page.', 'error');
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (examinerId, currentStatus, examinerName) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        const action = newStatus === 'active' ? 'activate' : 'deactivate';

        setModalConfig({
            isOpen: true,
            type: newStatus === 'active' ? 'success' : 'warning',
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} Examiner`,
            message: `Are you sure you want to ${action} ${examinerName}? ${
                newStatus === 'inactive'
                    ? 'They will not be able to login until reactivated.'
                    : 'They will be able to login and access the system.'
            }`,
            confirmText: action.charAt(0).toUpperCase() + action.slice(1),
            onConfirm: () => confirmStatusChange(examinerId, newStatus, action)
        });
    };

    const confirmStatusChange = async (examinerId, newStatus, action) => {
        try {
            await API.put(`/users/examiner/${examinerId}/status`, { status: newStatus });
            showToast(`Examiner ${action}d successfully`, 'success');
            fetchExaminers();
        } catch (error) {
            console.error('Error updating examiner status:', error);
            showToast(error.response?.data?.message || 'Failed to update examiner status', 'error');
        }
    };

    const filteredExaminers = examiners.filter(examiner => {
        if (filter === 'all') return true;
        return examiner.status === filter;
    });

    const stats = {
        total: examiners.length,
        active: examiners.filter(e => e.status === 'active').length,
        inactive: examiners.filter(e => e.status === 'inactive').length,
        pending: examiners.filter(e => e.status === 'pending').length
    };

    const statCards = [
        { key: 'all', icon: <UsersIcon />, colorClass: 'sme-stat-icon--blue', value: stats.total, label: 'Total Examiners' },
        { key: 'active', icon: <CheckIcon />, colorClass: 'sme-stat-icon--green', value: stats.active, label: 'Active' },
        { key: 'inactive', icon: <BanIcon />, colorClass: 'sme-stat-icon--slate', value: stats.inactive, label: 'Inactive' },
        { key: 'pending', icon: <ClockIcon />, colorClass: 'sme-stat-icon--amber', value: stats.pending, label: 'Pending' },
    ];

    const badgeClass = (status) =>
        status === 'active' ? 'sme-badge--success'
            : status === 'pending' ? 'sme-badge--warning'
                : 'sme-badge--danger';

    if (loading) {
        return (
            <div className="sme-page">
                <div className="sme-spinner-wrap">
                    <div className="sme-spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="sme-page">
            <div className="sme-page-header">
                <div>
                    <h1>My Examiners</h1>
                    <p>Manage examiners assigned to your subject</p>
                </div>
            </div>

            <div className="sme-stats-grid">
                {statCards.map(card => (
                    <div
                        key={card.key}
                        className={`sme-stat-card sme-stat-card--clickable ${filter === card.key ? 'sme-stat-card--selected' : ''}`}
                        onClick={() => setFilter(card.key)}
                    >
                        <div className={`sme-stat-icon ${card.colorClass}`}>{card.icon}</div>
                        <div>
                            <div className="sme-stat-value">{card.value}</div>
                            <div className="sme-stat-label">{card.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="sme-card">
                <div className="sme-card-header">
                    <h2>
                        {filter === 'all' ? 'All Examiners' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Examiners`}
                    </h2>
                    <span className="sme-badge sme-badge--primary">{filteredExaminers.length} Total</span>
                </div>
                {filteredExaminers.length > 0 ? (
                    <div className="sme-table-wrap">
                        <table className="sme-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>Joined</th>
                                    <th>Last Active</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredExaminers.map((examiner) => (
                                    <tr key={examiner.user_id}>
                                        <td className="sme-td-name">{examiner.name}</td>
                                        <td className="sme-muted">{examiner.email}</td>
                                        <td>
                                            <span className={`sme-badge ${badgeClass(examiner.status)}`}>
                                                {examiner.status}
                                            </span>
                                        </td>
                                        <td className="sme-muted">{new Date(examiner.created_at).toLocaleDateString()}</td>
                                        <td className="sme-muted">
                                            {examiner.last_login
                                                ? new Date(examiner.last_login).toLocaleDateString()
                                                : 'Never'}
                                        </td>
                                        <td>
                                            {examiner.status !== 'pending' ? (
                                                <button
                                                    className={`sme-btn sme-btn--sm ${examiner.status === 'active' ? 'sme-btn--danger' : 'sme-btn--success'}`}
                                                    onClick={() => handleStatusChange(examiner.user_id, examiner.status, examiner.name)}
                                                >
                                                    {examiner.status === 'active' ? 'Deactivate' : 'Activate'}
                                                </button>
                                            ) : (
                                                <span className="sme-muted" style={{ fontSize: '0.82rem' }}>Pending approval</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="sme-empty">
                        <UsersIcon />
                        <h3>No {filter !== 'all' ? filter : ''} examiners found</h3>
                        <p>
                            {filter === 'pending'
                                ? 'Pending examiners will appear here after registration'
                                : 'Examiners assigned to your subject will appear here'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig({ isOpen: false })}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                confirmText={modalConfig.confirmText}
            />
        </div>
    );
};

export default MyExaminers;
